import { Link } from '@tanstack/react-router';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { SevPill } from '@/components/brand/SevPill';
import { Kpi } from '@/components/brand/Kpi';
import { useAuth } from '@/lib/auth';
import { UnauthorizedState } from '@/components/brand/States';
import { useApiQuery } from '@/lib/hooks';
import { api, type CostsSummary, type SourceHealth } from '@/lib/api';
import { bucketedNow } from '@/lib/time';

interface AlertHistoryRow {
  id: string;
  ruleId: string;
  state: 'triggered' | 'resolved';
  triggeredAt: number;
  resolvedAt: number | null;
  acknowledgedAt: number | null;
  message: string;
}

function relativeMin(ms: number): string {
  const min = Math.max(0, Math.floor((Date.now() - ms) / 60_000));
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

export function DashboardPage() {
  const { me, isUnauthorized } = useAuth();

  // NB: every hook below must run on every render. Do NOT add an early
  // `return` above this line — bailing out before the useApiQuery calls
  // changes the hook count between renders (Rules of Hooks) and React
  // throws "rendered fewer hooks than expected", which with no
  // ErrorBoundary blanks the whole page. The isUnauthorized bail-out
  // lives after all hooks, just before the JSX return.
  const now = bucketedNow();
  const from30 = now - 30 * 24 * 60 * 60 * 1000;
  const from1d = now - 24 * 60 * 60 * 1000;

  const costsQ = useApiQuery<CostsSummary>({
    queryKey: ['dash/costs', from30, now],
    queryFn: () => api.costsSummary({ from: from30, to: now }),
  });
  const healthQ = useApiQuery<{ items: SourceHealth[] }>({
    queryKey: ['dash/health'],
    queryFn: () => api.integrationsHealth(),
  });
  const historyQ = useApiQuery<{ items: AlertHistoryRow[] }>({
    queryKey: ['dash/history', from1d, now],
    queryFn: async () => {
      const res = await fetch(`/api/alerts/history?from=${from1d}&to=${now}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });
  const automationsQ = useApiQuery<{ items: { status: string; cost_gbp: number | null }[] }>({
    queryKey: ['dash/automations', from1d, now],
    queryFn: async () => {
      const res = await fetch(`/api/automations?from=${from1d}&to=${now}&limit=500`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  const totalSpend = costsQ.data?.total_gbp ?? 0;
  const sparkSeries = costsQ.data?.per_day.map((d) => d.gbp) ?? [];

  const runs24h = automationsQ.data?.items.length ?? 0;
  const failed24h = automationsQ.data?.items.filter((e) => e.status === 'failed' || e.status === 'timeout').length ?? 0;

  const openAlerts = historyQ.data?.items.filter((h) => h.state === 'triggered' && !h.resolvedAt) ?? [];
  const openCritical = openAlerts.filter((h) => h.message.toLowerCase().includes('critical')).length;
  const oldest = openAlerts[openAlerts.length - 1];

  const integrationsCritical = healthQ.data?.items.filter((i) => i.status === 'critical').length ?? 0;
  const integrationsWarning = healthQ.data?.items.filter((i) => i.status === 'warning').length ?? 0;

  const heroVariant: 'crit' | 'warn' | 'quiet' =
    openCritical > 0 || integrationsCritical > 0
      ? 'crit'
      : openAlerts.length > 0 || integrationsWarning > 0
        ? 'warn'
        : 'quiet';

  if (isUnauthorized) return <UnauthorizedState />;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        meta={me ? <span className="mono">{me.email}</span> : null}
      />

      {/* HERO */}
      {heroVariant === 'crit' && (
        <Panel
          className="border-l-4 border-l-sev-crit"
          title="Action required"
          subtitle={oldest ? `oldest unresolved · ${relativeMin(oldest.triggeredAt)}` : undefined}
        >
          <div className="flex items-center gap-4 flex-wrap">
            <SevPill level="crit" label={`${openCritical || openAlerts.length} open`} />
            <p className="text-body">
              {oldest?.message ?? 'A rule has fired and is awaiting acknowledgement.'}
            </p>
            <Link
              to="/alerts"
              className="border-b-0 ml-auto bg-fg-default text-mb-spring-wood px-4 py-2 text-eyebrow"
            >
              Open alerts →
            </Link>
          </div>
        </Panel>
      )}
      {heroVariant === 'warn' && (
        <Panel className="border-l-4 border-l-sev-warn" title="Attention">
          <div className="flex items-center gap-4 flex-wrap">
            <SevPill level="warn" label={`${openAlerts.length} open`} />
            <p className="text-body">
              {oldest?.message ?? `${integrationsWarning} source(s) slower than expected.`}
            </p>
            <Link to="/alerts" className="border-b-0 ml-auto text-eyebrow text-fg-default border border-rule px-4 py-2">
              View →
            </Link>
          </div>
        </Panel>
      )}
      {heroVariant === 'quiet' && (
        <Panel title="All clear">
          <div className="flex items-center gap-4 flex-wrap">
            <SevPill level="ok" label="all clear" />
            <p className="text-fg-muted text-small">No open alerts. No source silent beyond its window.</p>
          </div>
        </Panel>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi
          label="MTD spend · GBP"
          value={
            <>
              <span className="text-fg-muted text-h3 align-top">£</span>
              {totalSpend.toFixed(2).split('.')[0]}
              <span className="text-fg-muted text-h3">.{totalSpend.toFixed(2).split('.')[1]}</span>
            </>
          }
          detail={`Last 30 days · ${costsQ.data?.per_service.length ?? 0} services`}
          spark={sparkSeries.length > 1 ? { points: sparkSeries, area: true } : undefined}
        />
        <Kpi
          label="Runs · 24 h"
          value={runs24h.toLocaleString()}
          detail={`across ${new Set(automationsQ.data?.items.map(() => 1)).size || 0} events`}
        />
        <Kpi
          label="Open alerts"
          value={openAlerts.length}
          detail={
            openAlerts.length === 0 ? (
              <SevPill level="ok" label="all clear" />
            ) : (
              <>
                {openCritical > 0 && <SevPill level="crit" label={`${openCritical} critical`} />}{' '}
                {openAlerts.length - openCritical > 0 && (
                  <SevPill level="warn" label={`${openAlerts.length - openCritical} other`} />
                )}
              </>
            )
          }
          variant={openCritical > 0 ? 'crit' : openAlerts.length > 0 ? 'warn' : 'ok'}
        />
        <Kpi
          label="Failed · 24 h"
          value={failed24h}
          detail={`of ${runs24h} runs`}
          variant={failed24h > 0 ? 'warn' : 'ok'}
        />
      </div>

      {/* Integration health */}
      <Panel title="Integrations" subtitle="Last event per source">
        {healthQ.data && (
          <ul className="divide-y divide-rule">
            {healthQ.data.items.map((it) => (
              <li key={it.source} className="flex items-center justify-between py-2 text-small">
                <span className="mono">{it.source}</span>
                <div className="flex items-center gap-4">
                  <span className="text-fg-muted text-caption">
                    {it.silent_for_sec == null
                      ? 'never'
                      : it.silent_for_sec < 60
                        ? `${it.silent_for_sec}s`
                        : it.silent_for_sec < 3600
                          ? `${Math.floor(it.silent_for_sec / 60)}m`
                          : `${Math.floor(it.silent_for_sec / 3600)}h`}
                  </span>
                  <SevPill
                    level={
                      it.status === 'critical'
                        ? 'crit'
                        : it.status === 'warning'
                          ? 'warn'
                          : it.status === 'no_data'
                            ? 'info'
                            : 'ok'
                    }
                    label={it.status}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
        {!healthQ.data && <p className="text-fg-muted text-small">Loading…</p>}
      </Panel>
    </div>
  );
}
